import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/contexts/AppContext";
import { Loader2, Lock, ShieldCheck, Clock, AlertCircle, CheckCircle, Wallet } from "lucide-react";
import { getFiatBalance, WalletFiatBalance, AddWithdraw } from "@/api/services/walletService";
import {
  operatorKycService,
  KycStatusResponse,
  KycDocumentType,
  parseKycError,
} from "@/api/services/operatorKycService";
import { getErrorMessage } from "@/utils/errorUtils";

// KX-39 (T-32): the operator's LOCAL-CURRENCY earnings panel. Shows the
// available/pending split, explains what unlocks the pending balance, and gates
// withdrawal behind KYC. The gate is enforced in backend code (403 KYC_REQUIRED);
// this panel surfaces that honestly and never fabricates a balance or a status.
// This is distinct from the KXT token balance.

const DOC_TYPES: KycDocumentType[] = ["id_front", "id_back", "proof_of_address", "selfie"];

const GOV_ID_TYPES = ["passport", "national_id", "drivers_license", "voters_card"] as const;

export default function FiatWithdrawPanel() {
  const { theme } = useApp();
  const { t } = useTranslation();

  const [balance, setBalance] = useState<WalletFiatBalance | null>(null);
  const [kyc, setKyc] = useState<KycStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Withdraw form
  const [amount, setAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState<"bank_transfer" | "mobile_money" | "crypto">("bank_transfer");
  const [withdrawTo, setWithdrawTo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankName, setBankName] = useState("");
  const [mobileNetwork, setMobileNetwork] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // KYC capture (shown when withdrawal is blocked / not verified)
  const [showKycForm, setShowKycForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [govIdType, setGovIdType] = useState<string>("national_id");
  const [idNumber, setIdNumber] = useState("");
  const [docFiles, setDocFiles] = useState<Partial<Record<KycDocumentType, File>>>({});
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [kycError, setKycError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [bal, k] = await Promise.all([
        getFiatBalance(),
        operatorKycService.getStatus(),
      ]);
      setBalance(bal);
      setKyc(k);
    } catch (e: unknown) {
      setLoadError(getErrorMessage(e) || t("operator.withdraw.errors.load", { defaultValue: "Couldn't load your balance." }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVerified = kyc?.status === "verified";
  const underReview = kyc?.status === "pending_review";
  const rejected = kyc?.status === "rejected";

  // Format a backend decimal string in the record currency. Display-only — no
  // arithmetic (AGENTS §2.1).
  const fmt = (decimal?: string, currency?: string): string => {
    if (!decimal || !currency) return "—";
    const n = Number(decimal);
    if (!Number.isFinite(n)) return `${decimal} ${currency}`;
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
    } catch {
      return `${decimal} ${currency}`;
    }
  };

  const panel = theme === "dark" ? "bg-[#1c1c1c] border-gray-800" : "bg-white border-gray-200";
  const sub = theme === "dark" ? "text-gray-400" : "text-gray-600";
  const strong = theme === "dark" ? "text-white" : "text-gray-900";
  const fld = `w-full rounded-lg border px-3 py-2 text-sm ${theme === "dark" ? "bg-[#111] border-gray-700 text-white" : "bg-gray-50 border-gray-300 text-gray-900"}`;

  const handleWithdraw = async () => {
    setMsg(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setMsg({ type: "error", text: t("operator.withdraw.errors.amount", { defaultValue: "Enter a valid amount." }) });
      return;
    }
    if (!withdrawTo.trim()) {
      setMsg({ type: "error", text: t("operator.withdraw.errors.destination", { defaultValue: "Enter where to send the funds." }) });
      return;
    }
    setSubmitting(true);
    try {
      // Full payload the /wallet/withdraw endpoint expects. The backend enforces
      // the KYC gate before touching the balance (KX-39 Q4).
      const payload: Record<string, unknown> = {
        amount: amt,
        currency: balance?.currency,
        withdrawTo: withdrawTo.trim(),
        withdrawMethod,
        ...(withdrawMethod === "bank_transfer"
          ? { bankname: bankName.trim(), accountname: accountName.trim() }
          : withdrawMethod === "mobile_money"
            ? { network: mobileNetwork.trim(), accountname: accountName.trim() }
            : {}),
      };
      await AddWithdraw(payload);
      setMsg({ type: "success", text: t("operator.withdraw.success", { defaultValue: "Withdrawal requested." }) });
      setAmount("");
      await load();
    } catch (e: unknown) {
      // KX-39 Q4: the backend blocks payout without verified KYC. Surface it and
      // open the KYC form — do NOT pre-block in the UI.
      const err = e as { response?: { status?: number; data?: { code?: string } } };
      if (err?.response?.status === 403 && err?.response?.data?.code === "KYC_REQUIRED") {
        setShowKycForm(true);
        setMsg({
          type: "error",
          text: t("operator.withdraw.kyc.requiredPrompt", {
            defaultValue: "Verify your identity to withdraw your earnings.",
          }),
        });
        // Refresh KYC status so the form reflects the latest state.
        try { setKyc(await operatorKycService.getStatus()); } catch { /* non-fatal */ }
      } else {
        setMsg({ type: "error", text: getErrorMessage(e) || t("operator.withdraw.errors.generic", { defaultValue: "Withdrawal failed. Please try again." }) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKycSubmit = async () => {
    setKycError("");
    if (!fullName.trim()) {
      setKycError(t("operator.withdraw.kyc.errors.name", { defaultValue: "Full name is required." }));
      return;
    }
    if (kyc?.governmentIdRequired && !idNumber.trim()) {
      setKycError(t("operator.withdraw.kyc.errors.idNumber", { defaultValue: "Government ID number is required." }));
      return;
    }
    const required = kyc?.requiredDocuments ?? DOC_TYPES;
    const missing = required.filter((d) => !docFiles[d]);
    if (missing.length > 0) {
      setKycError(
        t("operator.withdraw.kyc.errors.documents", {
          defaultValue: "Please upload all required documents.",
        })
      );
      return;
    }

    setKycSubmitting(true);
    try {
      // Upload each document, collect hosted URLs, then submit.
      const urls: Record<string, string> = {};
      for (const d of required) {
        const file = docFiles[d]!;
        urls[d] = await operatorKycService.uploadDocument(d, file);
      }
      const res = await operatorKycService.submit({
        fullName: fullName.trim(),
        governmentIdType: govIdType,
        idNumber: idNumber.trim(),
        documents: {
          id_front: urls["id_front"] || "",
          id_back: urls["id_back"] || "",
          proof_of_address: urls["proof_of_address"] || "",
          selfie: urls["selfie"] || "",
        },
      });
      setKyc((prev) => (prev ? { ...prev, status: res.status || "pending_review" } : prev));
      setShowKycForm(false);
      setMsg({
        type: "success",
        text: t("operator.withdraw.kyc.submitted", {
          defaultValue: "Identity submitted. We'll review it shortly, then you can withdraw.",
        }),
      });
    } catch (e: unknown) {
      const parsed = parseKycError(e);
      if (parsed.status === 409) {
        setKycError(t("operator.withdraw.kyc.errors.underReview", { defaultValue: "Your identity is already under review." }));
      } else if (parsed.code === "KYC_INCOMPLETE") {
        setKycError(parsed.message || t("operator.withdraw.kyc.errors.incomplete", { defaultValue: "Some documents are missing." }));
      } else {
        setKycError(parsed.message || t("operator.withdraw.kyc.errors.generic", { defaultValue: "Couldn't submit your identity. Please try again." }));
      }
    } finally {
      setKycSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-2xl border ${panel} p-8 flex items-center justify-center`}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("operator.withdraw.loading", { defaultValue: "Loading your balance…" })}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${panel} p-6 space-y-6`}>
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-[#2E5A88]" />
        <h2 className={`text-lg font-bold ${strong}`}>
          {t("operator.withdraw.title", { defaultValue: "Earnings & withdrawal" })}
        </h2>
      </div>

      {loadError && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" /> {loadError}
        </div>
      )}

      {/* Balances: available vs pending */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`rounded-xl border ${theme === "dark" ? "border-gray-800" : "border-gray-200"} p-4`}>
          <p className={`text-xs uppercase tracking-wide ${sub}`}>
            {t("operator.withdraw.available", { defaultValue: "Available to withdraw" })}
          </p>
          <p className={`text-2xl font-bold mt-1 ${strong}`}>{fmt(balance?.available, balance?.currency)}</p>
        </div>
        <div className={`rounded-xl border ${theme === "dark" ? "border-gray-800" : "border-gray-200"} p-4`}>
          <div className="flex items-center gap-1.5">
            <Clock className={`w-3.5 h-3.5 ${sub}`} />
            <p className={`text-xs uppercase tracking-wide ${sub}`}>
              {t("operator.withdraw.pending", { defaultValue: "Pending" })}
            </p>
          </div>
          <p className={`text-2xl font-bold mt-1 ${sub}`}>{fmt(balance?.pending, balance?.currency)}</p>
        </div>
      </div>

      {/* Pending-unlock prompt — only when KYC isn't verified yet. */}
      {!isVerified && (
        <div className={`flex items-start gap-3 rounded-xl border p-4 ${theme === "dark" ? "border-amber-900/50 bg-amber-950/20" : "border-amber-200 bg-amber-50"}`}>
          <Lock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className={`font-semibold ${strong}`}>
              {underReview
                ? t("operator.withdraw.kyc.underReviewTitle", { defaultValue: "Identity under review" })
                : rejected
                  ? t("operator.withdraw.kyc.rejectedTitle", { defaultValue: "Identity verification needed again" })
                  : t("operator.withdraw.kyc.lockedTitle", { defaultValue: "Verify your identity to unlock withdrawals" })}
            </p>
            <p className={sub}>
              {underReview
                ? t("operator.withdraw.kyc.underReviewBody", { defaultValue: "We're reviewing your documents. You'll be able to withdraw once verified." })
                : rejected
                  ? (kyc?.rejectionReason || t("operator.withdraw.kyc.rejectedBody", { defaultValue: "Your previous submission was not accepted. Please submit again." }))
                  : t("operator.withdraw.kyc.lockedBody", { defaultValue: "Your earnings accrue safely. Verifying your identity unlocks withdrawal." })}
            </p>
            {!underReview && (
              <button
                onClick={() => setShowKycForm((s) => !s)}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E5A88] hover:underline"
              >
                <ShieldCheck className="w-4 h-4" />
                {t("operator.withdraw.kyc.verifyCta", { defaultValue: "Verify identity" })}
              </button>
            )}
          </div>
        </div>
      )}

      {isVerified && (
        <div className={`flex items-center gap-2 text-sm rounded-xl border p-3 ${theme === "dark" ? "border-green-900/50 bg-green-950/20 text-green-400" : "border-green-200 bg-green-50 text-green-700"}`}>
          <ShieldCheck className="w-4 h-4 shrink-0" /> {t("operator.withdraw.kyc.verified", { defaultValue: "Identity verified — you can withdraw your available balance." })}
        </div>
      )}

      {msg && (
        <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${msg.type === "success" ? "text-green-700 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900" : "text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900"}`}>
          {msg.type === "success" ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />} {msg.text}
        </div>
      )}

      {/* Withdraw form — always allowed to attempt; the backend enforces the gate. */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={`block text-sm font-medium mb-1 ${strong}`}>
              {t("operator.withdraw.amountLabel", { defaultValue: "Amount to withdraw" })}
            </label>
            <input
              type="number"
              min={0}
              step="any"
              className={fld}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={balance?.currency ? `0.00 ${balance.currency}` : "0.00"}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${strong}`}>
              {t("operator.withdraw.methodLabel", { defaultValue: "Method" })}
            </label>
            <select
              className={fld}
              value={withdrawMethod}
              onChange={(e) => setWithdrawMethod(e.target.value as "bank_transfer" | "mobile_money" | "crypto")}
            >
              <option value="bank_transfer">{t("operator.withdraw.methods.bank_transfer", { defaultValue: "Bank transfer" })}</option>
              <option value="mobile_money">{t("operator.withdraw.methods.mobile_money", { defaultValue: "Mobile money" })}</option>
              <option value="crypto">{t("operator.withdraw.methods.crypto", { defaultValue: "Crypto" })}</option>
            </select>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${strong}`}>
            {withdrawMethod === "bank_transfer"
              ? t("operator.withdraw.destination.bank", { defaultValue: "Account number" })
              : withdrawMethod === "mobile_money"
                ? t("operator.withdraw.destination.mobile", { defaultValue: "Mobile number" })
                : t("operator.withdraw.destination.crypto", { defaultValue: "Wallet address" })}
          </label>
          <input className={fld} value={withdrawTo} onChange={(e) => setWithdrawTo(e.target.value)} />
        </div>

        {withdrawMethod === "bank_transfer" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${strong}`}>{t("operator.withdraw.bankName", { defaultValue: "Bank name" })}</label>
              <input className={fld} value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${strong}`}>{t("operator.withdraw.accountName", { defaultValue: "Account name" })}</label>
              <input className={fld} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
            </div>
          </div>
        )}
        {withdrawMethod === "mobile_money" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${strong}`}>{t("operator.withdraw.mobileNetwork", { defaultValue: "Mobile network" })}</label>
              <input className={fld} value={mobileNetwork} onChange={(e) => setMobileNetwork(e.target.value)} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${strong}`}>{t("operator.withdraw.accountName", { defaultValue: "Account name" })}</label>
              <input className={fld} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleWithdraw}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-[#2E5A88] hover:bg-[#244a73] text-white text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("operator.withdraw.submit", { defaultValue: "Withdraw" })}
          </button>
        </div>
      </div>

      {/* KYC capture form */}
      {showKycForm && !isVerified && !underReview && (
        <div className={`rounded-xl border p-4 space-y-4 ${theme === "dark" ? "border-gray-800" : "border-gray-200"}`}>
          <h3 className={`font-semibold ${strong}`}>
            {t("operator.withdraw.kyc.formTitle", { defaultValue: "Verify your identity" })}
          </h3>
          {kycError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {kycError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${sub}`}>{t("operator.withdraw.kyc.fullName", { defaultValue: "Full legal name" })}</label>
              <input className={fld} value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${sub}`}>{t("operator.withdraw.kyc.govIdType", { defaultValue: "Government ID type" })}</label>
              <select className={fld} value={govIdType} onChange={(e) => setGovIdType(e.target.value)}>
                {GOV_ID_TYPES.map((g) => (
                  <option key={g} value={g}>
                    {t(`operator.withdraw.kyc.govIdTypes.${g}`, { defaultValue: g.replace(/_/g, " ") })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={`block text-sm mb-1 ${sub}`}>{t("operator.withdraw.kyc.idNumber", { defaultValue: "Government ID number" })}</label>
            <input className={fld} value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(kyc?.requiredDocuments ?? DOC_TYPES).map((d) => (
              <div key={d}>
                <label className={`block text-sm mb-1 ${sub}`}>
                  {t(`operator.withdraw.kyc.docs.${d}`, { defaultValue: d.replace(/_/g, " ") })}
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className={`${fld} file:mr-3 file:rounded file:border-0 file:bg-[#2E5A88] file:px-3 file:py-1 file:text-white file:text-xs`}
                  onChange={(e) => setDocFiles((prev) => ({ ...prev, [d]: e.target.files?.[0] }))}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowKycForm(false)}
              className={`px-4 py-2 text-sm rounded-lg border ${theme === "dark" ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"}`}
            >
              {t("operator.withdraw.kyc.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              onClick={handleKycSubmit}
              disabled={kycSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm rounded-lg bg-[#2E5A88] hover:bg-[#244a73] text-white font-semibold disabled:opacity-50"
            >
              {kycSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {t("operator.withdraw.kyc.submit", { defaultValue: "Submit for verification" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
